// components/RoomAvailabilityCalendar.tsx - Fixed version
import { useState, useEffect } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaTimesCircle,
  FaCalendarCheck,
  FaClock,
  FaCalendarPlus,
  FaCalendarMinus,
} from "react-icons/fa";

interface AvailabilityCalendarProps {
  roomId: number;
  price: number;
  onDateSelect?: (date: Date | null) => void;
  onDateRangeSelect?: (checkIn: Date | null, checkOut: Date | null) => void;
  onPriceUpdate?: (total: number) => void;
  onBookNow?: () => void;
}

interface Booking {
  id: number;
  roomId: number;
  checkIn: Date;
  checkOut: Date;
  status: "confirmed" | "pending" | "cancelled";
}

export default function RoomAvailabilityCalendar({
  roomId,
  price,
  onDateSelect,
  onDateRangeSelect,
  onPriceUpdate,
  onBookNow,
}: AvailabilityCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalNights, setTotalNights] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  // Mock bookings data
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      const mockBookings: Booking[] = [
        {
          id: 1,
          roomId,
          checkIn: new Date(2024, 11, 10),
          checkOut: new Date(2024, 11, 15),
          status: "confirmed",
        },
        {
          id: 2,
          roomId,
          checkIn: new Date(2024, 11, 20),
          checkOut: new Date(2024, 11, 25),
          status: "confirmed",
        },
        {
          id: 3,
          roomId,
          checkIn: new Date(2024, 11, 5),
          checkOut: new Date(2024, 11, 7),
          status: "pending",
        },
      ];
      setBookings(mockBookings);
      setIsLoading(false);
    }, 500);
  }, [roomId]);

  // Update price when dates change
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      const nights = Math.ceil(
        (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      setTotalNights(nights);
      const total = nights * price;
      setTotalPrice(total);
      if (onPriceUpdate) {
        onPriceUpdate(total);
      }
      if (onDateRangeSelect) {
        onDateRangeSelect(checkInDate, checkOutDate);
      }
    } else {
      setTotalNights(0);
      setTotalPrice(0);
      if (onPriceUpdate) {
        onPriceUpdate(0);
      }
      if (onDateRangeSelect) {
        onDateRangeSelect(null, null);
      }
    }
  }, [checkInDate, checkOutDate, price]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  const getDayStatus = (day: number): "available" | "booked" | "pending" | "check-in" | "check-out" | "selected" => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, day);

    if (checkInDate && date.toDateString() === checkInDate.toDateString()) {
      return "check-in";
    }

    if (checkOutDate && date.toDateString() === checkOutDate.toDateString()) {
      return "check-out";
    }

    if (checkInDate && checkOutDate) {
      if (date > checkInDate && date < checkOutDate) {
        return "selected";
      }
    }

    const booking = bookings.find((b) => {
      const checkIn = new Date(b.checkIn);
      const checkOut = new Date(b.checkOut);
      return date >= checkIn && date < checkOut;
    });

    if (booking) {
      return booking.status === "pending" ? "pending" : "booked";
    }

    return "available";
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentDate.getMonth() &&
      today.getFullYear() === currentDate.getFullYear()
    );
  };

  const isPastDate = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isInRange = (day: number) => {
    if (!checkInDate || !checkOutDate) return false;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return date > checkInDate && date < checkOutDate;
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (isPastDate(day)) return;
    const status = getDayStatus(day);
    if (status === "booked") return;

    if (!checkInDate) {
      setCheckInDate(date);
      if (onDateSelect) onDateSelect(date);
      return;
    }

    if (checkInDate && !checkOutDate) {
      if (date.toDateString() === checkInDate.toDateString()) {
        setCheckInDate(null);
        if (onDateSelect) onDateSelect(null);
        return;
      }
      
      if (date > checkInDate) {
        setCheckOutDate(date);
        if (onDateSelect) onDateSelect(date);
      } else {
        setCheckInDate(date);
        setCheckOutDate(null);
        if (onDateSelect) onDateSelect(date);
      }
      return;
    }

    if (checkInDate && checkOutDate) {
      setCheckInDate(date);
      setCheckOutDate(null);
      if (onDateSelect) onDateSelect(date);
    }
  };

  const clearSelection = () => {
    setCheckInDate(null);
    setCheckOutDate(null);
    setTotalNights(0);
    setTotalPrice(0);
    if (onDateSelect) onDateSelect(null);
    if (onDateRangeSelect) onDateRangeSelect(null, null);
    if (onPriceUpdate) onPriceUpdate(0);
  };

  const changeMonth = (increment: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + increment, 1));
  };

  const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const availableDays = days.filter(day => {
    const status = getDayStatus(day);
    return status === "available" && !isPastDate(day);
  }).length;

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-primary text-xl font-bold text-gray-800">
            Availability
          </h3>
          <p className="font-secondary text-sm text-gray-500 mt-1">
            {availableDays} days available this month
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            aria-label="Previous month"
          >
            <FaChevronLeft className="text-gray-600" />
          </button>
          <span className="font-primary font-semibold text-gray-700 min-w-[140px] text-center">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button
            onClick={() => changeMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            aria-label="Next month"
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="text-center font-secondary text-xs font-semibold text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDayOfMonth }, (_, i) => (
              <div key={`empty-${i}`} className="h-12" />
            ))}

            {days.map((day) => {
              const status = getDayStatus(day);
              const past = isPastDate(day);
              const today = isToday(day);
              const isClickable = !past && status !== "booked";
              const inRange = isInRange(day);

              let bgColor = "";
              let textColor = "";
              let ring = "";

              if (status === "check-in") {
                bgColor = "bg-blue-600";
                textColor = "text-white";
                ring = "ring-2 ring-blue-400 ring-offset-2";
              } else if (status === "check-out") {
                bgColor = "bg-blue-600";
                textColor = "text-white";
                ring = "ring-2 ring-blue-400 ring-offset-2";
              } else if (status === "selected" || inRange) {
                bgColor = "bg-blue-100";
                textColor = "text-blue-700";
              } else if (status === "booked") {
                bgColor = "bg-red-100";
                textColor = "text-red-400";
              } else if (status === "pending") {
                bgColor = "bg-yellow-100";
                textColor = "text-yellow-600";
              } else if (past) {
                bgColor = "bg-gray-100";
                textColor = "text-gray-400";
              } else {
                bgColor = "hover:bg-green-50";
                textColor = "text-gray-700";
              }

              return (
                <button
                  key={day}
                  onClick={() => isClickable && handleDateClick(day)}
                  disabled={!isClickable}
                  className={`
                    relative h-12 rounded-lg transition-all duration-200
                    ${bgColor} ${textColor}
                    ${isClickable && status === "available" ? "hover:scale-105 hover:shadow-md" : ""}
                    ${ring}
                    ${today && !checkInDate && !checkOutDate ? "ring-2 ring-blue-400 ring-offset-1" : ""}
                    flex items-center justify-center font-secondary text-sm font-medium
                  `}
                >
                  {day}
                  {status === "booked" && (
                    <FaTimesCircle className="absolute -top-1 -right-1 text-red-500 text-xs" />
                  )}
                  {status === "pending" && (
                    <FaCircle className="absolute -top-1 -right-1 text-yellow-500 text-xs" />
                  )}
                  {status === "check-in" && (
                    <FaCalendarPlus className="absolute -top-1 -right-1 text-white text-xs" />
                  )}
                  {status === "check-out" && (
                    <FaCalendarMinus className="absolute -top-1 -right-1 text-white text-xs" />
                  )}
                  {today && status === "available" && !checkInDate && !checkOutDate && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-blue-600 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Selection Info & Price - Always visible when dates are selected */}
          {(checkInDate || checkOutDate) && (
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  {checkInDate && (
                    <div className="flex items-center gap-2">
                      <FaCalendarPlus className="text-blue-600 text-sm" />
                      <span className="font-secondary text-xs text-gray-500">Check-in:</span>
                      <span className="font-primary font-semibold text-sm text-gray-800">
                        {checkInDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                  {checkOutDate && (
                    <div className="flex items-center gap-2">
                      <FaCalendarMinus className="text-blue-600 text-sm" />
                      <span className="font-secondary text-xs text-gray-500">Check-out:</span>
                      <span className="font-primary font-semibold text-sm text-gray-800">
                        {checkOutDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                  {totalNights > 0 && (
                    <div className="flex items-center gap-2">
                      <FaClock className="text-blue-600 text-sm" />
                      <span className="font-secondary text-xs text-gray-500">Nights:</span>
                      <span className="font-primary font-semibold text-sm text-gray-800">
                        {totalNights} nights
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {totalPrice > 0 ? (
                    <>
                      <p className="font-secondary text-xs text-gray-500 uppercase tracking-wider">
                        Total Price
                      </p>
                      <p className="font-primary font-bold text-2xl text-blue-600">
                        ${totalPrice}
                      </p>
                    </>
                  ) : (
                    <p className="font-secondary text-xs text-gray-500">
                      Select dates to see price
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={clearSelection}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-secondary font-semibold py-2 px-4 rounded-lg transition-all duration-200 text-sm"
                >
                  Clear Dates
                </button>
                <button
                  onClick={onBookNow}
                  disabled={!checkInDate || !checkOutDate}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FaCalendarCheck className="text-sm" />
                  Book Now
                </button>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-secondary text-xs text-gray-600">Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                <span className="font-secondary text-xs text-gray-600">Selected</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-secondary text-xs text-gray-600">Booked</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="font-secondary text-xs text-gray-600">Pending</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                <span className="font-secondary text-xs text-gray-600">Past</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}